/**
 * POSSEN — Main navigation toggle (desktop).
 *
 * Shows and hides the navigation row (FW26, SS26, Suits, ...) from a menu
 * icon at the far left of the top row. Closed, the row slides up behind the
 * top strip AND the header genuinely shrinks — that is the point of the
 * control, and the reason it has to do more than toggle a class.
 *
 * Two things stood between "the row collapses" and "the page gets the space".
 *
 *   1. style.scss pinned .header to a hard 11.7rem with
 *      justify-content:center, so a collapsed row just re-centred the top
 *      strip inside an unchanged box. main-nav-toggle.css makes that height
 *      follow its content.
 *
 *   2. #main clears the fixed header via an inline margin-top that
 *      theme.liquid's setMarginTop() writes from the header's measured
 *      height — but only on $(document).ready and $(window).resize. A
 *      toggle is neither, so the content stayed exactly where it was even
 *      once the header above it got shorter. This file rewrites that offset
 *      (and --header-height, which header-scroll.css and
 *      collection-filters.css also read) on every toggle.
 *
 * The offset is set once to its target rather than re-measured every frame:
 * margin-top is a layout property, and rewriting it on a rAF loop would
 * reflow the whole document sixty times a second. The CSS gives #main the
 * same duration and the same curve as the row, so the two track each other.
 *
 * A disclosure button, not a popover: no Escape-to-close and no
 * outside-click-to-close. Collapsing a row of navigation is a layout choice
 * the customer made on purpose, not a transient panel that should vanish
 * because focus moved elsewhere.
 */
(function () {
  'use strict';

  /* Covers the slower direction (260ms open) plus a frame or two of slack. */
  var SETTLE_MS = 300;

  function init() {
    var toggle = document.querySelector('[data-main-nav-toggle]');
    var navWrap = document.querySelector('[data-main-nav]');
    if (!toggle || !navWrap) return;

    var root = document.documentElement;
    var header = document.querySelector('header.row.u-full-width');
    var main = document.getElementById('main');

    var settleTimer = null;

    /* Captured from the settled open state, which is what the page loads in.
       The closed target is simply the header without the row. */
    var openHeaderH = 0;
    var rowH = 0;

    function remeasure() {
      if (!header || navWrap.classList.contains('is-collapsed')) return;
      openHeaderH = Math.round(header.getBoundingClientRect().height);
      rowH = Math.round(navWrap.getBoundingClientRect().height);
    }

    function applyOffset(height) {
      if (!height) return;
      root.style.setProperty('--header-height', height + 'px');
      /* Inline, because that is how theme.liquid sets it — matching it here
         keeps one source of truth for the property rather than two rules
         fighting over specificity. */
      if (main) main.style.marginTop = height + 'px';
    }

    remeasure();
    /* The theme recomputes from the live header on resize, which is correct
       in either state once the height is content-driven. This only refreshes
       the cached open-state numbers so a later toggle aims at the right
       target. */
    window.addEventListener('resize', remeasure);

    toggle.addEventListener('click', function () {
      var willCollapse = !navWrap.classList.contains('is-collapsed');

      /* Measure before the class lands, so a toggle fired mid-animation
         still has honest numbers to work from. */
      if (!willCollapse && !openHeaderH) remeasure();

      navWrap.classList.add('is-animating');
      navWrap.classList.toggle('is-collapsed', willCollapse);
      toggle.setAttribute('aria-expanded', String(!willCollapse));

      root.classList.add('is-nav-toggling');
      root.classList.toggle('is-nav-closing', willCollapse);

      if (openHeaderH && rowH) {
        applyOffset(willCollapse ? openHeaderH - rowH : openHeaderH);
      }

      /* Clearing first means a rapid second click retargets the settle
         instead of un-clipping the row mid-travel. */
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(function () {
        navWrap.classList.remove('is-animating');
        root.classList.remove('is-nav-toggling');
        root.classList.remove('is-nav-closing');

        /* Re-anchor from reality. Whatever the arithmetic above predicted,
           the header has now settled at its real height — take that as the
           truth so rounding or a resize mid-toggle cannot accumulate drift. */
        if (header) {
          var live = Math.round(header.getBoundingClientRect().height);
          applyOffset(live);
          if (!navWrap.classList.contains('is-collapsed')) {
            openHeaderH = live;
            rowH = Math.round(navWrap.getBoundingClientRect().height);
          }
        }
      }, SETTLE_MS);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
